import { ChangeDetectionStrategy, Component, computed, effect, input, output, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { SharedModule } from '@aiwyn/shared';

/**
 * The response card's footer action row, shared by questionnaire and document
 * items (Figma "Text Response" / "Request Content" action rows):
 *
 * - DEFAULT state: "Mark as Not Applicable" + "I'm Finished", right-aligned.
 *   "I'm Finished" is inert until the item has a response to submit, and while
 *   one is still uploading.
 * - ANSWERED state (item submitted): a check + "Marked as Finished" indicator
 *   on the left, a link-style "Undo" on the right — disabled while the undo
 *   call is in flight (`isUndoPending`; undo is not optimistic).
 * - N/A state: a note that the item is not applicable, with its own link-style
 *   "Undo" on the same in-flight gate.
 * - "Mark as N/A" reveals an optional note field; confirming emits the note.
 *
 * There is intentionally NO request-level submit — the redesign is per-item.
 * Action availability is decided by the container's selector and passed in as
 * booleans (cxp-state: business rules in selectors, not the component).
 */
@Component({
  selector: 'cxp-crl-panel-item-actions',
  templateUrl: './crl-panel-item-actions.component.html',
  styleUrls: ['./crl-panel-item-actions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SharedModule, ReactiveFormsModule],
})
export class CrlPanelItemActionsComponent {
  readonly canMarkFinished = input<boolean>(false);
  /**
   * Whether the item reads as finished — the "Marked as Finished" indicator.
   * Separate from {@link canUndoFinished} because the firm accepting an item
   * takes the Undo away without making it any less finished.
   */
  readonly isMarkedFinished = input<boolean>(false);
  readonly canUndoFinished = input<boolean>(false);
  readonly canMarkNotApplicable = input<boolean>(false);
  /**
   * True when the item has no response to submit yet — a document item with no
   * uploaded file, or a questionnaire with no answer.
   */
  readonly isMissingResponse = input<boolean>(false);
  /**
   * True while a file is uploading TO THIS ITEM — uploads elsewhere in the
   * request never reach here. Finishing mid-upload would suggest the client can
   * walk away and still have the file arrive, so the button waits.
   */
  readonly isUploadingResponse = input<boolean>(false);
  readonly canUndoNotApplicable = input<boolean>(false);
  readonly isNotApplicable = input<boolean>(false);
  /** True while an undo call is in flight — the Undo links disable. */
  readonly isUndoPending = input<boolean>(false);
  /**
   * Which status-moving call is in flight, if any. Its button spins in place —
   * same size, label swapped for the spinner — and every action goes inert until
   * the server says which one the item ended up taking.
   */
  readonly pendingAction = input<'finish' | 'notApplicable' | null>(null);

  readonly markFinished = output<void>();
  readonly undoFinished = output<void>();
  /** Emits the optional N/A note (null when left blank). */
  readonly markNotApplicable = output<string | null>();
  readonly undoNotApplicable = output<void>();

  protected readonly isSubmitting = computed(() => this.pendingAction() !== null);
  protected readonly isFinishLoading = computed(() => this.pendingAction() === 'finish');
  protected readonly isNotApplicableLoading = computed(() => this.pendingAction() === 'notApplicable');

  /**
   * "I'm Finished" renders in all three cases but is inert. "Mark as Not
   * Applicable" stays available throughout — it is the other way to answer an
   * item, and an item must leave its open status by one route or the other.
   */
  protected readonly isFinishDisabled = computed(
    () => this.isMissingResponse() || this.isUploadingResponse() || this.isSubmitting(),
  );

  /**
   * Why the button is inert, as tooltip copy, or null for no tooltip. An upload
   * in flight is worth saying out loud; an empty response already speaks for
   * itself through the empty field or file list beside the button.
   */
  protected readonly finishDisabledHint = computed<string | null>(() =>
    this.isUploadingResponse() && !this.isSubmitting() ? 'Uploading files' : null,
  );

  /** Local UI: whether the N/A note field is showing. */
  protected readonly showNoteField = signal(false);
  protected readonly noteControl = new FormControl<string>('', { nonNullable: true });

  constructor() {
    // Reactive forms own the control's enabled state, so this cannot be a
    // `[disabled]` binding on the element.
    effect(() => {
      if (this.isSubmitting()) {
        this.noteControl.disable({ emitEvent: false });
      } else {
        this.noteControl.enable({ emitEvent: false });
      }
    });

    // The note form closes on the server's answer, not on the click, and closes
    // whichever way that answer goes — a failure has already said so in a
    // snackbar. The note text itself survives unless the item actually took it,
    // so a retry does not start from a blank field.
    effect(() => {
      const submitting = this.isNotApplicableLoading();
      if (submitting) {
        this.wasMarkingNotApplicable = true;
        return;
      }
      if (!this.wasMarkingNotApplicable) return;
      this.wasMarkingNotApplicable = false;
      this.showNoteField.set(false);
      if (this.isNotApplicable()) this.noteControl.reset();
    });
  }

  /** Whether the N/A call this component started is still outstanding. */
  private wasMarkingNotApplicable = false;

  onFinished(): void {
    this.markFinished.emit();
  }

  onUndoFinished(): void {
    this.undoFinished.emit();
  }

  onUndoNotApplicable(): void {
    this.undoNotApplicable.emit();
  }

  openNoteField(): void {
    this.showNoteField.set(true);
  }

  cancelNote(): void {
    this.showNoteField.set(false);
    this.noteControl.reset();
  }

  confirmNotApplicable(): void {
    const note = this.noteControl.value.trim();
    this.markNotApplicable.emit(note.length > 0 ? note : null);
    // The form stays up, inert, until the server answers; closing it here would
    // dismiss the note before anything had been done with it.
  }
}
